import React from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface StarRadarChartProps {
  scores: { [key: string]: number };
  chartMode: 'filled' | 'slashed';
  size?: 'compact' | 'full';
  /** Optional override for label font size (e.g. for dashboard). */
  labelFontSize?: number;
  /** Optional mapping of attribute keys to display names e.g. { spiritual: 'Spiritual', emotioPsychoRational: 'Emotio-Psycho-Rational' } */
  attributeLabels?: { [key: string]: string };
}

const StarRadarChart: React.FC<StarRadarChartProps> = ({ scores, chartMode, size = 'full', labelFontSize, attributeLabels }) => {
  const { isDarkMode } = useTheme();
  const attributes = Object.keys(scores);
  const values = Object.values(scores);
  // Responsive sizing: smaller on mobile and compact mode
  const isMobile = window.innerWidth < 768;
  const isSmallMobile = window.innerWidth < 480;
  const isTablet = window.innerWidth < 1024;
  const baseSize = size === 'compact'
    ? (isMobile ? 200 : 300)
    : (isSmallMobile ? 180 : isMobile ? 280 : isTablet ? 500 : 700);
  const chartSize = baseSize;
  const center = chartSize / 2;
  const maxValue = 4; // Maximum score is 4 stars
  const levels = 4; // 4 levels for 4-star system

  // Generate points for the radar chart
  const generatePoints = (value: number, index: number, total: number) => {
    const angle = (Math.PI * 2 * index) / total - Math.PI / 2; // Start from top
    const radius = (value / maxValue) * (chartSize * 0.4); // Scale to fit within chart
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return { x, y };
  };

  // Generate grid lines
  const generateGridPoints = (level: number) => {
    const points = [];
    for (let i = 0; i < attributes.length; i++) {
      const point = generatePoints(level, i, attributes.length);
      points.push(`${point.x},${point.y}`);
    }
    return points.join(' ');
  };

  // Generate axis lines
  const generateAxisPoints = (index: number) => {
    const point = generatePoints(maxValue, index, attributes.length);
    return `${center},${center} ${point.x},${point.y}`;
  };

  // Generate data polygon
  const dataPoints = values.map((value, index) => generatePoints(value, index, attributes.length));
  const dataPath = dataPoints.map(point => `${point.x},${point.y}`).join(' ') + ` ${dataPoints[0].x},${dataPoints[0].y}`;

  // Dark mode aware colors
  const gridColor = isDarkMode ? 'rgba(148, 163, 184, 0.3)' : 'rgba(255, 255, 255, 0.2)';
  const labelStrokeColor = isDarkMode ? '#0f172a' : 'white';
  const labelFillColor = isDarkMode ? '#cbd5e1' : '#1a3a3a';

  return (
    <div className="radar-chart-container">
      <svg
        width="100%"
        height="100%"
        viewBox={`-${size === 'full' ? 50 : 30} -${size === 'full' ? 50 : 30} ${chartSize + (size === 'full' ? 100 : 60)} ${chartSize + (size === 'full' ? 100 : 60)}`}
        className="radar-chart"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid lines */}
        {Array.from({ length: levels }, (_, i) => (
          <polygon
            key={i}
            points={generateGridPoints((i + 1) * (maxValue / levels))}
            fill="none"
            stroke={gridColor}
            strokeWidth="1"
            strokeDasharray="5,5"
          />
        ))}

        {/* Data polygon */}
        {chartMode === 'filled' ? (
          <polygon
            points={dataPoints.map(point => `${point.x},${point.y}`).join(' ')}
            fill="rgba(255, 255, 0, 0.2)"
            stroke="#f4c430"
            strokeWidth="3"
          />
        ) : (
          <>
            {/* Slashed outline mode */}
            <polygon
              points={dataPoints.map(point => `${point.x},${point.y}`).join(' ')}
              fill="none"
              stroke="#f4c430"
              strokeWidth="2"
              strokeDasharray="5"
            />
          </>
        )}

        {/* Star points */}
        {dataPoints.map((point, index) => (
          <g key={`star-${index}`} className="star-point">
            {/* Outer solid yellow circle (static) */}
            <circle
              cx={point.x}
              cy={point.y}
              r={size === 'full' ? "5" : "3"}
              fill="#f4c430"
              opacity="0.9"
              className="star-glow-outer"
            />
            {/* Inner solid yellow circle with slow glow */}
            <circle
              cx={point.x}
              cy={point.y}
              r={size === 'full' ? "10" : "6"}
              fill="#f4c430"
              opacity="1"
              className="star-glow"
            >
              <animate
                attributeName="r"
                values={`${size === 'full' ? "10;30;10" : "6;18;6"}`}
                dur="3s"
                repeatCount="indefinite"
                begin={`${index * 1}s`}
              />
              <animate
                attributeName="opacity"
                values="0.8;0.2;0.8"
                dur="3s"
                repeatCount="indefinite"
                begin={`${index * 1}s`}
              />
            </circle>
          </g>
        ))}

        {/* Labels */}
        {attributes.map((attribute, index) => {
          const point = generatePoints(maxValue + (size === 'full' ? 0.5 : 0.3), index, attributes.length);
          // Use provided attribute labels or fall back to processing the key
          const displayLabel = attributeLabels?.[attribute] || attribute.replace(/[-]/g, ' ').split(' ')[0];
          const fontSize = labelFontSize ?? (size === 'full' ? (isMobile ? 12 : 16) : (isMobile ? 10 : 12));
          return (
            <g key={attribute}>
              {/* Text stroke for better contrast */}
              <text
                x={point.x}
                y={point.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="none"
                stroke={labelStrokeColor}
                strokeWidth="3"
                fontSize={fontSize}
                fontWeight="700"
              >
                {displayLabel}
              </text>
              {/* Main text */}
              <text
                x={point.x}
                y={point.y}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={labelFillColor}
                fontSize={fontSize}
                fontWeight="700"
              >
                {displayLabel}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default StarRadarChart;

// Ensure this file is treated as a module
export { };

